import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { ACCOUNT_PLANS, planAmount, planCycleLabel, planStartLabel } from '../features/plans/accountPlans';
import { savePlanChoice } from '../features/plans/planChoice';

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [cycle, setCycle] = useState('monthly');
  const groups = ['Personal', 'Business'];

  const selectPlan = (planKey) => {
    savePlanChoice(planKey, cycle);
    const target = isAuthenticated ? '/billing' : '/register';
    navigate(`${target}?plan=${planKey}&cycle=${cycle}`);
  };

  return (
    <ErrorBoundary section="Pricing">
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <img src="/img/eloNestsymbol.png" alt="eloNest" className="h-8 w-8" />
                <span className="text-2xl font-bold text-gray-900">eloNest</span>
              </Link>
              <Link to="/login" className="text-gray-700 hover:text-gray-900 px-4 py-2 font-medium">
                Sign In
              </Link>
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Prices</h1>
            <p className="mt-3 text-lg text-gray-600">Choose Personal or Business, then create the account.</p>
            <p className="mt-2 text-sm text-gray-500">
              A card is required to start any plan. Personal includes a 15-day free trial: cancel before day 15 and you are not charged.
              Change or cancel your plan any time from Billing.
            </p>
            <div className="mt-6 inline-flex rounded-lg border border-gray-300 bg-white p-1">
              <button
                type="button"
                onClick={() => setCycle('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${cycle === 'monthly' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setCycle('annual')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${cycle === 'annual' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
              >
                Annual
              </button>
            </div>
          </div>

          {groups.map((group) => (
            <section key={group} className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">{group}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {ACCOUNT_PLANS.filter((plan) => plan.group === group).map((plan) => (
                  <article key={plan.key} className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                    <p className="mt-4 text-3xl font-bold text-gray-900">
                      ${planAmount(plan, cycle)}
                      <span className="text-base font-normal text-gray-500"> / {planCycleLabel(cycle)}</span>
                    </p>
                    <p className="mt-1 text-gray-600">
                      ${plan.monthlyPrice} a month or ${plan.annualPrice} a year
                    </p>
                    <ul className="mt-6 space-y-2 text-gray-700 flex-1">
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => selectPlan(plan.key)}
                      className="mt-8 w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
                    >
                      {planStartLabel(plan)}
                    </button>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </main>
      </div>
    </ErrorBoundary>
  );
};

export default Pricing;
